import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { VpnKey } from '@mui/icons-material';
import { dataverseService } from '../services/dataverseService';
import { listKeysByLock, revokeKeysForLock } from '../services/unlocKeyService';
import { getDataverseErrorMessage } from '../lib/dataverseErrorHandler';
import { formatKeyTime, mapUnlocHoldersForLock } from '../lib/phoneKeys';
import KeyRequestDialog from './KeyRequestDialog';
import { KEY_REQUEST_SCOPE } from '../config/keyRequest';

export default function LockKeysDialog({ open, lock, onClose }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [holders, setHolders] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [grantOpen, setGrantOpen] = useState(false);

  const loadHolders = async () => {
    if (!lock?.lockId) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const [listed, phones] = await Promise.all([
        listKeysByLock(lock.lockId),
        dataverseService.phoneService.getAll()
      ]);

      if (!listed) {
        return;
      }

      setHolders(mapUnlocHoldersForLock(listed, phones, lock.lockId));
      setSelected(new Set());
    } catch (loadError) {
      setHolders([]);
      setError(getDataverseErrorMessage(loadError, 'Kunne ikke hente tilgang for låsen.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !lock?.lockId) {
      return;
    }

    loadHolders();
  }, [open, lock?.lockId]);

  const toggle = (holderId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(holderId)) {
        next.delete(holderId);
      } else {
        next.add(holderId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((current) =>
      current.size === holders.length ? new Set() : new Set(holders.map((holder) => holder.id))
    );
  };

  const runRevokes = async (holderIds, all) => {
    const targets = all ? holders : holders.filter((holder) => holderIds.includes(holder.id));
    const withMobile = targets.filter((holder) => holder.mobileNumber);

    if (withMobile.length === 0) {
      setError('Fant ingen mobilnummer å trekke tilbake for.');
      return;
    }

    const confirmed = window.confirm(
      all
        ? `Trekker tilbake alle Unloc-nøkler for ${lock?.name || 'låsen'}. Fortsette?`
        : `Trekker tilbake nøkler for ${withMobile.length} valgt(e) for ${lock?.name || 'låsen'}. Fortsette?`
    );
    if (!confirmed) {
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      for (const holder of withMobile) {
        const result = await revokeKeysForLock(holder.mobileNumber, lock.lockId);
        if (!result) {
          return;
        }
      }

      setSuccessMessage(
        all ? 'Alle nøkler for låsen er trukket tilbake.' : 'Valgte nøkler er trukket tilbake.'
      );
      await loadHolders();
    } catch (revokeError) {
      setError(getDataverseErrorMessage(revokeError, 'Kunne ikke trekke tilbake nøkler.'));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = selected.size;
  const allSelected = holders.length > 0 && selectedCount === holders.length;

  return (
    <>
      <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKey color="primary" />
          Tilgang til {lock?.name || 'lås'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Gyldige nøkler hentet live fra Unloc. Lagres ikke i OpenCare.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : holders.length === 0 ? (
            <Alert severity="info">Ingen gyldige nøkler funnet for denne låsen.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedCount > 0 && !allSelected}
                      onChange={toggleAll}
                      disabled={submitting}
                    />
                  </TableCell>
                  <TableCell>Navn</TableCell>
                  <TableCell>Mobil</TableCell>
                  <TableCell>Utstedt</TableCell>
                  <TableCell>Gyldig til</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {holders.map((holder) => (
                  <TableRow key={holder.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(holder.id)}
                        onChange={() => toggle(holder.id)}
                        disabled={submitting}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {holder.holderName}
                        {holder.registered && (
                          <Chip label="Registrert" size="small" variant="outlined" />
                        )}
                        {holder.keyCount > 1 && (
                          <Chip label={`${holder.keyCount} nøkler`} size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{holder.mobileNumber || '—'}</TableCell>
                    <TableCell>{formatKeyTime(holder.grantedOn)}</TableCell>
                    <TableCell>{holder.expiresOn ? formatKeyTime(holder.expiresOn) : 'Varig'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={onClose} disabled={submitting}>
            Lukk
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" disabled={submitting} onClick={() => setGrantOpen(true)}>
            Gi nøkkel
          </Button>
          <Button
            color="warning"
            variant="outlined"
            disabled={submitting || selectedCount === 0}
            onClick={() => runRevokes([...selected], false)}
          >
            {submitting ? 'Behandler…' : `Trekk tilbake valgte (${selectedCount})`}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={submitting || holders.length === 0}
            onClick={() => runRevokes([], true)}
          >
            Trekk tilbake alle
          </Button>
        </DialogActions>
      </Dialog>

      <KeyRequestDialog
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        scope={KEY_REQUEST_SCOPE.SINGLE_LOCK}
        lock={lock}
        onSuccess={() => {
          setGrantOpen(false);
          loadHolders();
        }}
      />
    </>
  );
}
